import '../styles.css';
import { useState } from 'react';
import { Box, Flex, SimpleGrid } from '@chakra-ui/react';

import { FilterRemoveIcon } from '@assets/icons/index';
import { initialFilterState, initialSearchState } from './const';

import Select from '@components/filter/select';
import ButtonOutline from '@components/buttons/buttonOutline';
import ButtonRegular from '@components/buttons/buttonRegular';
import { TextBold, TextRegular } from '@components/typography';
import { debounce } from '@helpers/utils';

import useGetDC from '@domain/dc/useGetDC';
import useGetBrands from '@domain/brands/useGetBrands';
import { useFilter } from '@pages/SelloutMonitoring/hooks/state/filters';
import checkFilterInformation from '@pages/SelloutMonitoring/helpers/checkFilterInformation';
import { useGetInfiniteListMasterProduct } from '@pages/UploadMappingV2/hooks/query/useGetListMasterProduct';
import { isEmpty } from 'lodash';

const FilterList = ({ isOpen, onClose }) => {
    const { setFilter, resetFilter, onClearFilter, ...rest } = useFilter();
    const [tempFilter, setTempFilter] = useState(initialFilterState);
    const isOpenFilterInformation = checkFilterInformation(rest);
    const [search, setSearch] = useState(initialSearchState);

    const brands = useGetBrands({ enabled: !!search?.brand, search: search?.brand });
    const dc = useGetDC({ enabled: !!search?.dc, search: search?.dc });
    const product = useGetInfiniteListMasterProduct({
        search: `${search?.product}`,
        enabled: !isEmpty(search?.product),
    });

    const onCloseOptions = () => setSearch(initialSearchState);
    const onChangeFilter = (key, val) => setTempFilter({ ...tempFilter, [key]: [val] });

    const handleClearFilter = (key) => () => {
        setFilter({ ...tempFilter, [key]: [] });
        setTempFilter({ ...tempFilter, [key]: [] });
        setSearch((prev) => ({ ...prev, [key]: initialSearchState[key] }));
    };

    const handleSearch = (field) => (val) => {
        setSearch((prev) => ({
            ...prev,
            [field]: val,
        }));
    };

    const FILTER_CONFIG = [
        {
            key: 'brand',
            title: 'Brand',
            placeholder: 'Cari dan pilih brand',
            source: brands,
            onSearch: handleSearch('brand'),
            onClearOptions: handleClearFilter('brand'),
        },
        {
            key: 'product',
            title: 'Product',
            placeholder: 'Cari dan pilih nama atau barcode produk',
            source: {
                ...product,
                data: product?.data?.map((item) => ({
                    id: item.barcode,
                    name: item.name,
                })),
            },
            onSearch: handleSearch('product'),
            onClearOptions: handleClearFilter('product'),
        },
        {
            key: 'agg',
            title: 'Aggregation Level',
            placeholder: 'Pilih level aggregation',
            source: {
                data: [
                    { id: 'daily', name: 'Daily' },
                    { id: 'monthly', name: 'Monthly' },
                    { id: 'invoice', name: 'Invoice' },
                ],
            },
            onSearch: handleSearch('agg'),
            onClearOptions: handleClearFilter('agg'),
        },
        {
            key: 'dc',
            title: 'Distribution Center',
            placeholder: 'Cari dan pilih DC',
            source: dc,
            onSearch: handleSearch('dc'),
            onClearOptions: handleClearFilter('dc'),
        },
    ];

    const onResetFilter = () => {
        setTempFilter(initialFilterState);
        onClearFilter();
        onClose();
    };

    const onApplyFilter = () => {
        setFilter({
            brand: tempFilter?.brand[0]?.map((item) => item?.name),
            product: tempFilter?.product[0]?.map((item) => item?.id),
            agg: tempFilter?.agg[0]?.map((item) => item?.id),
            dc: tempFilter?.dc[0]?.map((item) => item?.name),
        });
        onClose();
    };

    const onRemoveFilterItem = (key) => {
        setFilter({ [key]: [] });
        setTempFilter({ ...tempFilter, [key]: [] });
    };

    return (
        <Box className="filters__container">
            {isOpen && (
                <Flex className="box-filters fade-animation-bottom" direction="row">
                    <SimpleGrid columns={{ base: 2 }} gap="24px" w="100%">
                        {FILTER_CONFIG.map((cfg) => (
                            <Select
                                key={cfg.key}
                                title={cfg.title}
                                placeholder={cfg.placeholder}
                                selected={tempFilter?.[cfg.key]?.[0] ?? []}
                                data={cfg.source?.data}
                                isLoading={cfg.source?.isLoading}
                                refetchData={cfg.source?.refetch}
                                onSearch={debounce(cfg.onSearch)}
                                onCloseOptions={onCloseOptions}
                                onChangeFilter={(val) => onChangeFilter(cfg.key, val)}
                                isMultiple={true}
                                onClearOptions={cfg.onClearOptions}
                            />
                        ))}
                    </SimpleGrid>
                    <Flex className="filter-buttons__container">
                        <ButtonOutline
                            component="Reset All"
                            width="79px"
                            height="36px"
                            color="var(--color-primary)"
                            onClick={onResetFilter}
                        />
                        <ButtonRegular
                            component="Apply Filter"
                            width="98px"
                            height="36px"
                            color="var(--color-primary)"
                            onClick={onApplyFilter}
                        />
                    </Flex>
                </Flex>
            )}

            {!isOpen && isOpenFilterInformation && (
                <Flex className="filter-selected__container" align="center" wrap="wrap" gap={2}>
                    <TextBold>Filters by:</TextBold>
                    {FILTER_CONFIG.map(
                        (cfg) =>
                            rest?.[cfg.key]?.length > 0 && (
                                <Flex key={cfg.key} className="filter-selected__item" align="center">
                                    <TextBold>{cfg.title}:</TextBold>
                                    <TextRegular className="selected-text" mx={1}>
                                        {rest[cfg.key].length} Terpilih
                                    </TextRegular>
                                    <FilterRemoveIcon onClick={() => onRemoveFilterItem(cfg.key)} />
                                </Flex>
                            ),
                    )}
                    <ButtonOutline
                        component="Reset All"
                        width="79px"
                        height="36px"
                        color="var(--color-primary)"
                        onClick={onResetFilter}
                    />
                </Flex>
            )}
        </Box>
    );
};

export default FilterList;
